import React from 'react';
import { useNavigate } from 'react-router-dom';

import { FILLS, FONTS, RADII, SEARCH_LAYER } from '../../styles/tokens';
import type { SurfaceTone } from '../../styles/tokens';
import { ListingSuggestion } from './useWhereSuggestions';

interface ListingSuggestionGroupProps {
  listings: ListingSuggestion[];
  /** The group heading, which names what the matches can be. */
  heading: string;
  /** Called before navigating, so the caller can close its sheet or popover. */
  onNavigate: () => void;
  surface?: SurfaceTone;
}

const ROW_HEIGHT = 56;
const TILE_SIZE = 44;

const KIND_LABEL: Record<ListingSuggestion['kind'], string> = {
  accommodation: 'Stay',
  service: 'Service',
};

const DETAIL_PATH: Record<ListingSuggestion['kind'], string> = {
  accommodation: '/property',
  service: '/service',
};

const IconSvg: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const HouseIcon: React.FC = () => (
  <IconSvg>
    <path d="M3 11l9-7 9 7" />
    <path d="M5 10v10h14V10" />
  </IconSvg>
);

export const BellIcon: React.FC = () => (
  <IconSvg>
    <path d="M4 17h16" />
    <path d="M6 17a6 6 0 0 1 12 0" />
    <path d="M12 8V6" />
    <path d="M3 20h18" />
  </IconSvg>
);

/** The typed-match suggestion rows shared by the phone and desktop Where fields. */
const ListingSuggestionGroup: React.FC<ListingSuggestionGroupProps> = ({
  listings,
  heading,
  onNavigate,
  surface = 'dark',
}) => {
  const navigate = useNavigate();
  const isCream = surface === 'cream';
  if (listings.length === 0) return null;

  return (
    <div>
      <div className="suggestion-group-heading" data-surface={isCream ? 'cream' : undefined}>
        {heading}
      </div>
      {listings.map((listing) => (
        <button
          key={`${listing.kind}-${listing.id}`}
          type="button"
          className="focus-ring"
          onClick={() => {
            onNavigate();
            navigate(`${DETAIL_PATH[listing.kind]}/${listing.id}`);
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            width: '100%',
            minHeight: ROW_HEIGHT,
            padding: '6px 4px',
            background: 'none',
            border: 'none',
            textAlign: 'left',
            color: isCream ? SEARCH_LAYER.ink : FILLS.textPrimary,
            fontFamily: FONTS.body,
            cursor: 'pointer',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: TILE_SIZE,
              height: TILE_SIZE,
              flexShrink: 0,
              borderRadius: RADII.iconBox,
              background: isCream ? SEARCH_LAYER.card : FILLS.chip,
              // On cream the tile sits on a card-fill card in the phone sheet, so a stroke keeps its shape.
              border: isCream ? `1px solid ${SEARCH_LAYER.stroke}` : undefined,
              boxSizing: 'border-box',
              display: 'grid',
              placeItems: 'center',
              color: isCream ? SEARCH_LAYER.inkSecondary : FILLS.textLight,
            }}
          >
            {listing.kind === 'service' ? <BellIcon /> : <HouseIcon />}
          </span>
          <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span
              style={{
                fontSize: 15,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {listing.name}
            </span>
            <span
              style={{ fontSize: 13, color: isCream ? SEARCH_LAYER.inkMuted : FILLS.textMuted }}
            >
              {KIND_LABEL[listing.kind]}
              {listing.location ? ` · ${listing.location}` : ''}
            </span>
          </span>
        </button>
      ))}
    </div>
  );
};

export default ListingSuggestionGroup;
